/**
 * Copyright (c) 2021-present, NDLA.
 *
 * This source code is licensed under the GPLv3 license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { styled } from "@ndla/styled-system/jsx";
import parse from "html-react-parser";
import { useEffect, useRef } from "react";
import { urlIsNDLAUrl } from "../../util/ndlaUrl";

interface Props {
  html: string;
  url: string;
}

const IframeWrapper = styled("div", {
  base: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "full",
    maxWidth: "full",
    "& > iframe": {
      width: "full",
      minHeight: "surface.medium",
      tablet: {
        borderRadius: "xsmall",
      },
    },
  },
});

export const LearningpathIframe = ({ html, url }: Props) => {
  const iframeRef = useRef<HTMLInputElement>(null);

  const getIframeDOM = () => {
    return iframeRef.current?.children[0] as HTMLIFrameElement;
  };

  const handleScrollTo = (evt: MessageEvent) => {
    const iframe = getIframeDOM();
    if (iframe) {
      const rect = iframe.getBoundingClientRect();
      const scrollTop = window.scrollY || document.documentElement.scrollTop;

      const top = evt.data.top + rect.top + scrollTop;
      window.scroll({ top });
    }
  };

  const handleResize = (evt: MessageEvent) => {
    if (!evt.data.height) {
      return;
    }
    const iframe = getIframeDOM();
    if (iframe) {
      const newHeight = parseInt(evt.data.height, 10);
      iframe.style.height = `${newHeight}px`;
    }
  };

  const handleIframeMessages = (event: MessageEvent) => {
    const iframe = getIframeDOM();
    /* Needed to enforce content to stay within iframe on Safari iOS */
    if (iframe) {
      iframe.setAttribute("scrolling", "no");
    }

    if (!event || !event.data) {
      return;
    }

    switch (event.data.event) {
      case "resize":
        handleResize(event);
        break;
      case "scrollTo":
        handleScrollTo(event);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (!urlIsNDLAUrl(url)) return;
    window.addEventListener("message", handleIframeMessages);
    return () => window.removeEventListener("message", handleIframeMessages);
  });

  return <IframeWrapper ref={iframeRef}>{parse(html)}</IframeWrapper>;
};
